import {useCallback} from 'react';

import {AdCallbacksExtendedFactory} from '@shared/Ads/Yandex/types';

import {getEventPayloadForAd} from '@shared/Ads/Yandex/utils/getEventPayloadForAd';
import {once} from '@shared/Ads/Yandex/utils/once';
import {trackImpressions} from '@shared/Ads/Yandex/utils/trackImpressions';

import {
  useYandexAdsContextSelector,
  YandexAdsContextCommonSelectors,
} from '../../context/YandexAdsContext';

type Props = {
  getRefreshNumber: () => number;
};

export const useStats = ({getRefreshNumber}: Props) => {
  const addEventBase = useYandexAdsContextSelector(
    YandexAdsContextCommonSelectors.getEmitAdFoxEvent,
  );
  const createCallbacks = useCallback<AdCallbacksExtendedFactory>(
    ({code, containerId, cleanupCallbacks}) => {
      const addEvent = (eventName: string, data?: Record<string, string>) =>
        addEventBase({
          eventName,
          payload: {...getEventPayloadForAd({containerId, code}), ...data},
        });

      const initImpressionsTracking = once((data: AdFox.RenderData) => {
        const element =
          data.stickyAdNode || document.getElementById(containerId);
        if (element && window.IntersectionObserver) {
          cleanupCallbacks.push(
            ...trackImpressions(element, (imp_type) =>
              addEvent('AdFoxImpEvent', {imp_type}),
            ),
          );
        }
      });

      const trackSessionWithAds = once<void>(() => {
        addEvent('AdFoxSessionWithAds');
      });

      return {
        onLoad: (data) => {
          trackSessionWithAds();
          addEvent('AdFoxOnLoad');
          addEvent('AdResponseBids', {
            refresh_number: `${getRefreshNumber()}`,
            bid: data?.bundleParams?.data?.impressionData?.revenue || '',
          });
        },
        onRender: (data) => {
          addEvent('AdFoxOnRender');
          initImpressionsTracking(data);
        },
        onClose: () => {
          addEvent('AdFoxOnClose');
        },
        onError: () => {
          addEvent('AdFoxOnError');
        },
        onStub: () => {
          addEvent('AdFoxOnStub');
        },
        onClick: () => {
          addEvent('AdFoxOnClick');
        },
      };
    },
    [addEventBase, getRefreshNumber],
  );
  return createCallbacks;
};
